import { apiClient } from '@/shared/api/client';
import type { ApiResponse } from '@/shared/types/api.types';
import { malaysiaScraping, type SsmLoginResponse } from './malaysiaScraping';
import type { IncorporateReservationPayload } from '../components/incorporate-reservation/composables/useFormPayload';

export type { SsmLoginResponse };
export type { IncorporateReservationPayload };

/**
 * Reservation Director Item (예약에 등록된 프로모터/디렉터)
 */
export interface ReservationDirectorItem {
  identification_number: string;
  person_name: string;
  identification_type?: string;
  nationality?: string;
  date_of_birth?: string;
  gender?: string;
  race?: string;
  email?: string;
  mobile_number?: string;
  office_number?: string;
  fax_number?: string;
  residential_address?: {
    country?: string;
    state?: string;
    city?: string;
    post_code?: string;
    street01?: string;
    street02?: string;
    street03?: string;
  } | null;
  raw_cells?: string[];
}

/**
 * Reservation Item (예약 목록 항목)
 */
export interface ReservationItem {
  entity_name: string;
  reservation_number: string;
  status: string;
  directors?: ReservationDirectorItem[];
}

/**
 * My Application Item (전체 신청 이력 항목)
 */
export interface MyApplicationItem {
  entity_name: string;
  reservation_number: string;
  status: string;
  purpose: string;
  company_type: string;
}

/**
 * Incorporation Application Item (법인 설립 목록 항목)
 */
export interface IncorporationApplicationItem {
  company_name: string;
  registration_no: string;
  incorporation_date: string;
  purpose: string;
  company_type: string;
  status: string;
  action_text: string;
  action_url: string;
}

/**
 * Incorporation List Response
 */
export interface IncorporationListResponse {
  success: boolean;
  incorporations: IncorporationApplicationItem[];
  error?: string;
}

/**
 * Reservation List Response
 */
export interface ReservationListResponse {
  success: boolean;
  reservations: ReservationItem[];
  all_applications?: MyApplicationItem[];
  error?: string;
}

/**
 * Incorporate Reservation API
 * Malaysia 예약명 법인 설립 전용 API
 *
 * Endpoint: /api/marketplace/v1/malaysia/entity/incorporate-reservation
 */

/**
 * Incorporate Reservation 응답 타입
 */
/**
 * Dry Run 검증 탭 데이터
 */
export interface DryRunVerificationTab {
  items?: Array<Record<string, string>>;
  count?: number;
  empty?: boolean;
  [key: string]: any;
}

export interface DryRunVerification {
  tabs: {
    application_details?: Record<string, string>;
    directors?: DryRunVerificationTab;
    shareholders?: DryRunVerificationTab;
    information_to_agency?: Record<string, string>;
  };
  parse_error?: string;
}

export interface IncorporateReservationResponse {
  success: boolean;
  error?: string;
  html?: string;
  pdfLength?: number;
  pdfDataBase64?: string;
  warning?: string;
  dry_run_verification?: DryRunVerification;
}

/**
 * Incorporate Reservation API
 */
export const incorporateReservationApi = {
  /**
   * SSM 로그인 (공통 API 재사용)
   */
  ssmLogin: (credentialId: string): Promise<SsmLoginResponse> => {
    return malaysiaScraping.ssmLogin(credentialId, 'mycoid2016');
  },

  /**
   * SSM 로그아웃
   */
  ssmLogout: (sessionId: string) => {
    return malaysiaScraping.ssmLogout(sessionId);
  },

  /**
   * 예약 목록 조회 (APPROVED 상태만)
   * ssmLogin()으로 획득한 session_id를 전달해야 합니다.
   */
  getReservationList: async (sessionId: string): Promise<ReservationListResponse> => {
    const response = await apiClient.post<ReservationListResponse>(
      '/marketplace/v1/malaysia/entity/reservation-list',
      { session_id: sessionId, include_details: true }
    );
    return (response as any)?.data || response;
  },

  /**
   * 법인 설립 목록 조회 (My Application > Incorporation)
   * ssmLogin()으로 획득한 session_id를 전달해야 합니다.
   */
  getIncorporationList: async (credentialId: string): Promise<IncorporationListResponse> => {
    // CRS 전환: crs_credential_id 전달 → 백엔드가 토큰 해석. 응답 companies(등록완료)를 목록으로 매핑.
    const response = await apiClient.post<any>(
      '/marketplace/v1/malaysia/entity/incorporation-list',
      { crs_credential_id: credentialId }
    );
    const data = (response as any)?.data || response;
    // CRS rocIncorporation/list 응답 → 목록 매핑 (Lodging Ref / Proposed Name / Reg No / Purpose / Type / Status)
    const incorporations: IncorporationApplicationItem[] = (data?.applications || []).map((a: any) => ({
      company_name: a.company_name || '',
      registration_no: a.registration_no || a.lodging_ref_no || '',
      incorporation_date: '',
      purpose: a.purpose || '',
      company_type: a.company_type || a.type_of_application || '',
      status: a.status || '',
      action_text: '',
      action_url: '',
    }));
    return { success: data?.success ?? false, incorporations, error: data?.error };
  },

  /**
   * Incorporate Reservation 실행
   */
  incorporateReservation: async (data: IncorporateReservationPayload): Promise<ApiResponse<IncorporateReservationResponse>> => {
    console.log('[IncorporateReservationApi] Request:', {
      reservedEntityName: data.reserved_entity_name,
      directorsCount: data.directors?.length,
      shareholdersCount: data.shareholders?.length,
      businessCodesCount: data.business_codes?.length,
    });

    const response = await apiClient.post<IncorporateReservationResponse>(
      '/marketplace/v1/malaysia/entity/incorporate-reservation',
      data
    );

    console.log('[IncorporateReservationApi] Response:', {
      success: (response as any)?.success || (response as any)?.data?.success
    });

    return response;
  },
};

export default incorporateReservationApi;
